import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, fmtErr } from "../lib/api";
import { toast } from "sonner";
import { Gear, Ruler, Package, FileText, CheckCircle, Play, ArrowsClockwise, Warning } from "@phosphor-icons/react";
import { inr } from "./bom/CostSummary";

const KIND_META = {
  design: { icon: Gear, color: "var(--c-accent)", label: "DESIGN" },
  drawing: { icon: Ruler, color: "#0891b2", label: "DRAWING" },
  bom: { icon: Package, color: "var(--c-amber)", label: "BOM" },
  document: { icon: FileText, color: "#7c3aed", label: "DOCUMENT" },
  approval: { icon: CheckCircle, color: "var(--c-green)", label: "APPROVAL" },
  stage: { icon: Play, color: "var(--c-muted)", label: "STAGE" },
};
const KIND_TAB = { design: "design", drawing: "drawings", bom: "bom", document: "documents" };

export default function RevisionsTab({ projectId }) {
  const [data, setData] = useState(null);
  const [impact, setImpact] = useState(null);
  const [filter, setFilter] = useState(new Set());
  const [srcFilter, setSrcFilter] = useState("");
  const [sel, setSel] = useState(new Set());
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();

  const load = useCallback(async () => {
    try {
      const [t, i] = await Promise.all([
        api.get(`/projects/${projectId}/revisions-timeline`),
        api.get(`/projects/${projectId}/impact-analysis`),
      ]);
      setData(t.data);
      setImpact(i.data);
      setSel(new Set((i.data.stale || []).map((s) => s.id)));
    } catch (e) { toast.error(fmtErr(e)); }
  }, [projectId]);
  useEffect(() => { load(); }, [load]);

  const events = useMemo(() => {
    if (!data) return [];
    return data.events.filter((e) =>
      (!filter.size || filter.has(e.kind)) && (!srcFilter || e.source === srcFilter));
  }, [data, filter, srcFilter]);

  const regen = async (ids) => {
    setBusy(true);
    try {
      const { data: r } = await api.post(`/projects/${projectId}/regenerate-stale`, { ids: [...ids] }, { timeout: 180000 });
      toast.success(`Regenerated ${r.regenerated.length} artifact(s)` + (r.skipped.length ? ` · ${r.skipped.length} skipped (locked)` : ""));
      load();
    } catch (e) { toast.error(fmtErr(e)); } finally { setBusy(false); }
  };

  const toggle = (kind) => setFilter((f) => { const n = new Set(f); n.has(kind) ? n.delete(kind) : n.add(kind); return n; });

  if (!data) return <div className="p-8 text-center text-[var(--c-muted)] text-sm">Loading…</div>;
  const stale = impact?.stale || [];

  return (
    <div className="space-y-3" data-testid="revisions-tab">
      {/* summary bar */}
      <div className="bg-[var(--c-surface)] border border-[var(--c-border)] rounded-lg shadow-sm p-3 flex items-center gap-4 flex-wrap" data-testid="rev-summary">
        <span className="text-[11px] text-[var(--c-text)]"><b>{data.events.length}</b> events</span>
        {Object.entries(data.counts).filter(([, v]) => v).map(([k, v]) => (
          <span key={k} className="text-[10px] font-mono" style={{ color: KIND_META[k].color }}>
            {KIND_META[k].label.toLowerCase()}: {v}
          </span>
        ))}
        <span className="ml-auto text-[10px] font-mono text-[var(--c-muted)]">
          sources: {Object.entries(data.source_counts).map(([s, n]) => `${s}: ${n}`).join("  ")}
        </span>
      </div>

      {/* stale artifacts panel */}
      {stale.length > 0 && (
        <div className="bg-[var(--c-amber)]/10 border border-[var(--c-amber)]/40 rounded-sm p-3" data-testid="stale-panel">
          <div className="flex items-center gap-2 text-[11px] text-[var(--c-text)]">
            <Warning size={13} className="text-[var(--c-amber)]" />
            Design v{impact.current_design_version} made {impact.stale_totals.drawings} drawing(s), {impact.stale_totals.bom} BOM, {impact.stale_totals.documents} document(s) stale
          </div>
          <div className="mt-2 space-y-1">
            {stale.map((s) => (
              <label key={s.id + s.type} className="flex items-center gap-2 text-[11px] text-[var(--c-muted)]">
                <input type="checkbox" checked={sel.has(s.id)} data-testid={`stale-check-${s.id}`}
                  onChange={() => setSel((p) => { const n = new Set(p); n.has(s.id) ? n.delete(s.id) : n.add(s.id); return n; })} />
                <span style={{ color: KIND_META[s.type]?.color }}>{KIND_META[s.type]?.label}</span>
                {s.name} (built from design v{s.source_version ?? "?"}) {s.status === "approved" || s.status === "released" ? "🔒" : ""}
              </label>
            ))}
          </div>
          <div className="flex gap-2 mt-2">
            <button data-testid="regen-all-btn" onClick={() => regen(new Set(stale.map((s) => s.id)))} disabled={busy}
              className="h-6 px-2.5 bg-[var(--c-amber)] text-white text-[10px] rounded-sm flex items-center gap-1 disabled:opacity-40">
              <ArrowsClockwise size={11} className={busy ? "animate-spin" : ""} /> Regenerate All Stale
            </button>
            <button data-testid="regen-selected-btn" onClick={() => regen(sel)} disabled={busy || !sel.size}
              className="h-6 px-2.5 border border-[var(--c-amber)]/50 text-[var(--c-amber)] text-[10px] rounded-sm disabled:opacity-40">
              Regenerate Selected ({sel.size})
            </button>
          </div>
        </div>
      )}

      {/* filters */}
      <div className="flex items-center gap-1.5 flex-wrap" data-testid="rev-filters">
        {Object.entries(KIND_META).map(([k, m]) => (
          <button key={k} data-testid={`filter-${k}`} onClick={() => toggle(k)}
            className={`h-6 px-2 rounded-sm text-[10px] font-mono border transition-colors ${
              !filter.size || filter.has(k) ? "border-[var(--c-accent)]/60 text-[var(--c-text)] bg-[var(--c-accent)]/15" : "border-[var(--c-border)] text-[var(--c-muted)]"}`}>
            {m.label}
          </button>
        ))}
        <select data-testid="source-filter" value={srcFilter} onChange={(e) => setSrcFilter(e.target.value)}
          className="ml-auto h-6 bg-[var(--c-bg)] border border-[var(--c-border)] text-[var(--c-text)] text-[10px] rounded-sm px-1.5">
          <option value="">All sources</option>
          {[...new Set(data.events.map((e) => e.source).filter(Boolean))].map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      {/* timeline */}
      <div className="space-y-2" data-testid="rev-timeline">
        {events.map((e, i) => {
          const m = KIND_META[e.kind];
          const Icon = m.icon;
          return (
            <div key={i} className="bg-[var(--c-surface)] border border-[var(--c-border)] rounded-lg shadow-sm p-3" data-testid={`rev-event-${e.kind}`}>
              <div className="flex items-center gap-2 flex-wrap">
                <Icon size={13} style={{ color: m.color }} />
                <span className="font-mono text-[9px] uppercase tracking-wider" style={{ color: m.color }}>{m.label}</span>
                <span className="text-[11px] text-[var(--c-text)]">
                  {e.kind === "design" && `v${e.version} — ${e.label}`}
                  {e.kind === "drawing" && `${e.name} — Rev ${e.rev} · ${e.status}`}
                  {e.kind === "bom" && `v${e.version} — ${e.label}`}
                  {e.kind === "document" && `${e.name} — Rev ${e.rev}`}
                  {(e.kind === "approval" || e.kind === "stage") && e.detail}
                </span>
                <span className="ml-auto font-mono text-[9px] text-[var(--c-muted)]">
                  {String(e.at || "").slice(0, 16).replace("T", " ")} · {e.by || "system"} · {e.source}
                  {e.action === "stage_auto_advanced" && " [AUTO]"}
                </span>
              </div>
              {e.description && <div className="text-[10px] text-[var(--c-muted)] mt-1 italic">“{e.description}”</div>}
              {e.kind === "design" && e.changes?.length > 0 && (
                <div className="mt-2 border-t border-[var(--c-border)]/50 pt-2 space-y-0.5" data-testid="rev-changes">
                  {e.changes.map((c) => (
                    <div key={c.field} className="flex gap-2 font-mono text-[10px]">
                      <span className="text-[var(--c-muted)] w-36 truncate">{c.field}</span>
                      <span className="text-[var(--c-red)]">{JSON.stringify(c.before)}</span>
                      <span className="text-[var(--c-muted)]">→</span>
                      <span className="text-[var(--c-green)]">{JSON.stringify(c.after)}</span>
                    </div>
                  ))}
                </div>
              )}
              {e.kind === "bom" && e.summary && (
                <div className="mt-1 font-mono text-[10px] text-[var(--c-muted)]">
                  {e.summary.item_count} items · {Number(e.summary.total_weight || 0).toLocaleString()} kg · {inr(e.summary.total_cost)}
                </div>
              )}
              {KIND_TAB[e.kind] && (
                <button className="mt-1.5 text-[10px] text-[var(--c-accent)] hover:underline"
                  onClick={() => nav(`/projects/${projectId}?tab=${KIND_TAB[e.kind]}`)}
                  data-testid={`rev-goto-${e.kind}`}>
                  View in {KIND_TAB[e.kind]} tab →
                </button>
              )}
            </div>
          );
        })}
        {!events.length && <div className="text-center text-[11px] text-[var(--c-muted)] py-8">No revision events yet.</div>}
      </div>
    </div>
  );
}
